import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button, Input } from "antd";
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import axios from "axios";
import { getUrl } from "../../config";

const NavbarStatsClient = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [clients, setClients] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [menuVisible, setMenuVisible] = useState(false);
    const [fetchAll, setFetchAll] = useState(false);
    const menuRef = useRef(null);
    const iconRef = useRef(null);

    const fetchClients = async () => {
        try {
            const response = await axios.post(`${getUrl()}/api/getAllClientsStats`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
                params: { search: searchTerm },
            });
            if (response.data.status === "success") {
                // Si on cherche ou si on a demandé tous les clients, afficher tous les résultats
                if (searchTerm.trim() !== "" || fetchAll) {
                    setClients(response.data.data);
                } else {
                    // Sinon, limiter à 10 clients
                    setClients(response.data.data.slice(0, 10));
                }
            } else {
                console.error('Erreur lors de la récupération des clients: ', response.data.message);
            }
        } catch (error) {
            console.error("Erreur lors de la récupération des clients:", error);
        }
    };

    useEffect(() => {
        fetchClients();
    }, []);

    useEffect(() => {
        if (menuVisible) {
            fetchClients();
        }
    }, [menuVisible]);

    useEffect(() => {
        fetchClients();
    }, [searchTerm, fetchAll]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuVisible && menuRef.current && !menuRef.current.contains(event.target) && !iconRef.current.contains(event.target)) {
                setMenuVisible(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [menuVisible]);

    const handleClientClick = (clientId) => {
        navigate(`/informations/client/${clientId}`);
        setMenuVisible(false);
    };

    const handleIconClick = () => {
        setMenuVisible(!menuVisible);
        setFetchAll(false);  // Réinitialiser à 10 clients quand on ouvre le menu
    };

    const handleShowAllClick = () => {
        setFetchAll(true);
    };

    const filteredClients = clients.filter(client =>
        client.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div style={{ display: 'inline-block', position: 'relative' }}>
            <Button
                ref={iconRef}
                icon={<QueryStatsIcon />}
                style={{ 
                    background: 'none', 
                    color: 'white', 
                    fontSize: '20px', 
                    border: 'none', 
                    padding: 0,
                    outline: 'none',
                }}
                onClick={handleIconClick}
            />
            {menuVisible && (
                <div 
                    ref={menuRef}
                    style={{ 
                        position: 'absolute', 
                        backgroundColor: '#233044',
                        border: '1px solid #1a1f24',
                        zIndex: 1, 
                        maxHeight: '400px', 
                        overflowY: 'auto', 
                        width: '300px', 
                        padding: '10px',
                        right: 0,
                        top: '100%',
                        borderRadius: '4px',
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#1a1f24 #233044',
                    }}
                >
                    <style>
                        {`
                            ::-webkit-scrollbar {
                                width: 8px;
                            }
                            ::-webkit-scrollbar-track {
                                background: #233044;
                            }
                            ::-webkit-scrollbar-thumb {
                                background-color: #1a1f24;
                                border-radius: 4px;
                                border: 2px solid #233044;
                            }
                            ::-webkit-scrollbar-thumb:hover {
                                background-color: #2c3757;
                            }
                        `}
                    </style>
                    <Input
                        placeholder={t("Rechercher un client")}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ 
                            margin: '8px 0', 
                            width: '100%', 
                            borderRadius: '4px',
                            border: '1px solid rgb(209, 209, 209)',
                            backgroundColor: '#233044',
                            color: 'white',
                            fontSize: '16px',
                            padding: '8px'
                        }}
                    />
                    {filteredClients.length === 0 ? (
                        <div style={{ textAlign: 'center', color: 'white', padding: '10px' }}>
                            {t("Aucun client trouvé")}
                        </div>
                    ) : (
                        <>
                            <ul style={{ listStyleType: 'none', padding: 0 }}>
                                {filteredClients.map((client) => (
                                    <li 
                                        key={client.id} 
                                        onClick={() => handleClientClick(client.id)} 
                                        style={{ 
                                            cursor: 'pointer', 
                                            color: 'white', 
                                            padding: '8px', 
                                            borderBottom: '1px solid #1a1f24',
                                            transition: 'background-color 0.3s',
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1a1f24'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    >
                                        {client.name} - {client.city} - {client.zip_code}
                                    </li>
                                ))}
                            </ul>
                            {!fetchAll && searchTerm === "" && (
                                <Button 
                                    onClick={handleShowAllClick}
                                    style={{
                                        width: '100%',
                                        marginTop: '10px',
                                        backgroundColor: '#1a1f24',
                                        color: 'white',
                                        border: 'none',
                                    }}
                                >
                                    {t("Afficher tous les clients")}
                                </Button>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default NavbarStatsClient;
